/**
 * Asynchronous dialog functions for both browsers and terminals.
 *
 * This includes `alert`, `confirm`, `prompt` and other non-standard dialogs.
 * @experimental
 * @module
 */
import { isBrowserWindow, isDeno, isNodeLike } from "./env.ts";
import { throwUnsupportedRuntimeError } from "./error.ts";
import progress from "./dialog/progress.ts";
export { progress };
export * from "./dialog/progress.ts";
export * from "./dialog/file.ts";
/**
 * Displays a dialog with a message, and to wait until the user dismisses the
 * dialog.
 *
 * @example
 * ```ts
 * import { alert } from "@ayonli/jsext/dialog";
 *
 * await alert("Hello, world!");
 * ```
 */
export async function alert(message, options = {}) {
    if (isBrowserWindow) {
        const { alert } = await import("./dialog/web.ts");
        await alert(message, options);
    }
    else if (isDeno || isNodeLike) {
        const { default: alert } = await import("./dialog/cli/alert.ts");
        await alert(message, options);
    }
    else {
        throwUnsupportedRuntimeError();
    }
}
/**
 * Displays a dialog with a message, and to wait until the user either confirms
 * or cancels the dialog.
 *
 * @example
 * ```ts
 * import { confirm } from "@ayonli/jsext/dialog";
 *
 * if (await confirm("Are you sure?")) {
 *     console.log("Confirmed");
 * } else {
 *     console.log("Canceled");
 * }
 * ```
 */
export async function confirm(message, options = {}) {
    if (isBrowserWindow) {
        const { confirm } = await import("./dialog/web.ts");
        return await confirm(message, options);
    }
    else if (isDeno || isNodeLike) {
        const { default: confirm } = await import("./dialog/cli/confirm.ts");
        return await confirm(message, options);
    }
    else {
        throwUnsupportedRuntimeError();
    }
}
export async function prompt(message, options = "") {
    var _a, _b, _c;
    const defaultValue = typeof options === "string"
        ? options
        : options.defaultValue;
    const type = typeof options === "object"
        ? (_a = options.type) !== null && _a !== void 0 ? _a : "text"
        : "text";
    const mask = type === "password"
        ? typeof options === "object" ? ((_b = options.mask) !== null && _b !== void 0 ? _b : "*") : "*"
        : undefined;
    const gui = typeof options === "object" ? ((_c = options.gui) !== null && _c !== void 0 ? _c : false) : false;
    const timeout = typeof options === "object" ? options.timeout : undefined;
    if (isBrowserWindow) {
        const { prompt } = await import("./dialog/web.ts");
        return await prompt(message, { defaultValue, type, timeout });
    }
    else if (isDeno || isNodeLike) {
        const { default: prompt } = await import("./dialog/cli/prompt.ts");
        return await prompt(message, { defaultValue, type, mask, gui, timeout });
    }
    else {
        throwUnsupportedRuntimeError();
    }
}
//# sourceMappingURL=dialog.js.map